export const getSocialPresenceState = (status = {}) => {
  const ongoingWorkout = status.ongoingWorkouts?.[0] || null;

  if (ongoingWorkout) {
    return {
      key: 'working-out',
      label: 'WORKING OUT',
      workoutName: ongoingWorkout.name || 'Workout in progress',
      workout: ongoingWorkout,
    };
  }

  if (status.online) {
    return {
      key: 'online',
      label: 'ONLINE',
      workoutName: null,
      workout: null,
    };
  }

  return {
    key: 'offline',
    label: status.lastSeen ? null : 'OFFLINE',
    workoutName: null,
    workout: null,
  };
};

export const getPresenceStatusText = (status = {}, formatRelativeTime) => {
  const presence = getSocialPresenceState(status);
  if (presence.key !== 'offline') return presence.label;
  return status.lastSeen && formatRelativeTime ? formatRelativeTime(status.lastSeen) : presence.label;
};

export const shouldApplyActivityRevision = (existing = {}, incoming = {}) => {
  const incomingRevision = Number(incoming.completion_time_revision || 0);
  return Number(existing.completion_time_revision || 0) <= incomingRevision;
};

export const mergeSocialPresenceByDate = (previous = {}, message = {}, getDateKey) => {
  const activity = message.activity;
  const actor = message.user;
  if (!activity || !actor?._id || typeof getDateKey !== 'function') return previous;

  const incomingRevision = Number(activity.completion_time_revision || 0);
  const existingRevision = Object.values(previous).reduce((highest, day) => {
    for (const audience of ['friends', 'dojo']) {
      for (const person of day?.[audience] || []) {
        if (String(person.userId) === String(actor._id)
            && String(person.activity?.id) === String(activity._id)) {
          highest = Math.max(highest, Number(person.activity?.completion_time_revision || 0));
        }
      }
    }
    return highest;
  }, 0);
  if (!shouldApplyActivityRevision(
    { completion_time_revision: existingRevision },
    { completion_time_revision: incomingRevision },
  )) return previous;

  const correctedCompletion = message.event === 'updated' && activity.completed_at;
  const dateValue = correctedCompletion || activity.created_at || activity.completed_at || new Date();
  const dateKey = getDateKey(dateValue);
  const next = { ...previous };

  if (message.event === 'updated') {
    for (const [key, existingDay] of Object.entries(next)) {
      const day = { ...(existingDay || {}) };
      let changed = false;
      for (const audience of ['friends', 'dojo']) {
        const people = day[audience] || [];
        const filtered = people.filter(person => !(
          String(person.userId) === String(actor._id)
          && String(person.activity?.id) === String(activity._id)
        ));
        if (filtered.length !== people.length) {
          day[audience] = filtered;
          changed = true;
        }
      }
      if (changed) next[key] = day;
    }
  }

  const day = { ...(next[dateKey] || { friends: [], dojo: [] }) };
  const presence = {
    userId: String(actor._id),
    displayName: actor.display_name || 'Member',
    avatarUrl: actor.avatar_url || null,
    state: message.event === 'started' ? 'ongoing' : 'completed',
    activity: {
      id: activity._id,
      type: activity.type,
      name: activity.name || activity.type,
      ...(incomingRevision ? { completion_time_revision: incomingRevision } : {}),
    },
  };

  for (const audience of ['friends', 'dojo']) {
    const people = [...(day[audience] || [])];
    const index = people.findIndex(person => String(person.userId) === String(actor._id));
    if (message.event === 'cancelled' || message.event === 'deleted') {
      if (index >= 0) people.splice(index, 1);
    } else if (index >= 0) {
      people[index] = { ...people[index], ...presence };
    } else {
      people.unshift(presence);
    }
    day[audience] = people;
  }

  next[dateKey] = day;
  return next;
};
